<template>
  <section class="full-width-image-section completed-trails gray-bg">
    <div class="container">
      <div class="text-center">
        <h1 class="trails-header special-header">Completed Trails</h1>
      </div>
      <div class="info-box">
        <p v-if="isRegistered">
          Your email <strong>{{ userEmail }}</strong> is registered for badges.
        </p>
        <p v-else>
          Your email is not registered for badges.
          <router-link to="/register/0">Register here</router-link>.
        </p>
        <p>
          This system tracks your walks and awards badges based on your
          activities. Make sure to register your email to start earning badges
          and enjoy the rewards!
        </p>
      </div>

      <Section>
        <div class="badge-gallery">
          <!-- Milestone Badge -->
          <div v-if="badges.milestone" class="badge earned">
            <img
              :src="`/assets/badges/${badges.milestone}.jpg`"
              :alt="`${badges.milestone} Walks Badge`"
            />
            <div class="share-button-wrapper">
              <a
                @click.prevent="
                  sharePage(`/assets/badges/${badges.milestone}.jpg`)
                "
              >
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
          </div>

          <!-- Completion Badges -->
          <div v-if="badges.easy" class="badge earned">
            <img src="/assets/badges/easy.jpg" alt="All Easy Walks Badge" />
            <div class="share-button-wrapper">
              <a @click.prevent="sharePage('/assets/badges/easy.jpg')">
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
          </div>
          <div v-if="badges.moderate" class="badge earned">
            <img
              src="/assets/badges/moderate.jpg"
              alt="All Moderate Walks Badge"
            />
            <div class="share-button-wrapper">
              <a @click.prevent="sharePage('/assets/badges/moderate.jpg')">
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
          </div>
          <div v-if="badges.strenuous" class="badge earned">
            <img
              src="/assets/badges/strenuous.jpg"
              alt="All Strenuous Walks Badge"
            />
            <div class="share-button-wrapper">
              <a @click.prevent="sharePage('/assets/badges/strenuous.jpg')">
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
          </div>

          <!-- Special Badge -->
          <div v-if="badges.all" class="badge special-badge earned">
            <img src="/assets/badges/all.jpg" alt="All Walks Badge" />
            <div class="share-button-wrapper">
              <a @click.prevent="sharePage('/assets/badges/all.jpg')">
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
          </div>
        </div>
      </Section>

      <div class="completed-trails-table" v-if="walks.length">
        <div class="completed-trails-head">
          <div class="completed-trails-col"></div>
          <div class="completed-trails-col">Trail</div>
          <div class="completed-trails-col">Completed</div>
        </div>
        <div
          class="completed-trails-body"
          style="position: relative"
          v-for="walk in walks"
          :key="walk.DateTime"
        >
          <div class="completed-trails-col">
            <div class="share-button-wrapper2" style="">
              <a @click.prevent="sharePageWalk(walk.Name)">
                <img src="/assets/img/share-alt.svg" alt="Share" />
              </a>
            </div>
            <!-- <img src="/assets/img/completed.svg" />-->
          </div>
          <div class="completed-trails-col" style="text-align: left">
            {{ walk.Name }}
          </div>
          <div class="completed-trails-col-grade">
            {{ walk.Grading }}
          </div>
          <div class="completed-trails-col">
            {{ formatDate(walk.DateTime) }}
          </div>
        </div>
      </div>
    </div>
  </section>

  <div class="summary-section" hidden>
    <h3>Walk Summary</h3>
    <p>Easy Walks: {{ easyCount }}</p>
    <p>Moderate Walks: {{ moderateCount }}</p>
    <p>Strenuous Walks: {{ strenuousCount }}</p>
  </div>

  <!-- REGISTER FOR WALKS FORM - Compoonents-->
  <UserForm />

  <section class="full-width-image-section footer green-sep"></section>
</template>

<script>
import UserForm from "@/components/Userform.vue";
import db from "@/db";
export default {
  name: "CompletedWalks",
  components: {
    UserForm,
  },

  data() {
    return {
      walks: [],
      easyCount: 0,
      moderateCount: 0,
      strenuousCount: 0,
      totalCount: 0,
      badges: {
        milestone: null, // 5, 10, 20, or 40 walks
        easy: false,
        moderate: false,
        strenuous: false,
        all: false,
      },
      userEmail: "",
      isRegistered: false,
    };
  },
  async created() {
    try {
      // Fetch user from IndexedDB
      const user = await db.user.get(1);

      if (user && user.email) {
        this.userEmail = user.email;
        this.isRegistered = true;
      } else {
        this.userEmail = "";
        this.isRegistered = false;
      }

      // Fetch completed walks after checking registration
      this.fetchCompletedWalks();
    } catch (error) {
      console.error("Error fetching user email from IndexedDB:", error);
    }
  },
  methods: {
    async fetchCompletedWalks() {
      try {
        const customerId = localStorage.getItem("tenantId"); // Replace with the actual CustomerId
        const base64Credentials = btoa("Ian:Ennistymon1!");
        const baseURL = process.env.VUE_APP_API_BASE_URL;

        const response = await fetch(
          `${baseURL}VisitorCard/GetCompletedWalks?userId=${this.userEmail}&customerId=${customerId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              CustomerId: customerId,
              Authorization: `Basic ${base64Credentials}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch completed walks.");
        }

        //const responseText = await response.text();
        const data = await response.json();

        // Update data properties with the API response
        this.walks = data.Walks || [];
        this.easyCount = data.EasyWalksCount || 0;
        this.moderateCount = data.ModerateWalksCount || 0;
        this.strenuousCount = data.StrenuousWalksCount || 0;

        // Calculate badges
        this.calculateBadges(data);
      } catch (error) {
        console.error(error);
      }
    },
    calculateBadges(data) {
      // Count the number of completed easy, moderate, and strenuous walks
      const easyWalks = this.walks.filter(
        walk => walk.Grading.toUpperCase() === "EASY"
      ).length;
      const moderateWalks = this.walks.filter(
        walk => walk.Grading.toUpperCase() === "MODERATE"
      ).length;
      const strenuousWalks = this.walks.filter(
        walk => walk.Grading.toUpperCase() === "STRENUOUS"
      ).length;

      // Determine the highest milestone badge the user qualifies for
      this.totalCount =
        Number(easyWalks) + Number(moderateWalks) + Number(strenuousWalks);
      if (this.totalCount >= 40) {
        this.badges.milestone = 40;
      } else if (this.totalCount >= 20) {
        this.badges.milestone = 20;
      } else if (this.totalCount >= 10) {
        this.badges.milestone = 10;
      } else if (this.totalCount >= 5) {
        this.badges.milestone = 5;
      } else {
        this.badges.milestone = null;
      }

      // Completion badges
      this.badges.easy = Number(easyWalks) >= Number(data.EasyWalksCount);
      console.log(
        "Easy Walks:",
        easyWalks,
        "Total Easy Walks:",
        data.EasyWalksCount
      );

      this.badges.moderate =
        Number(moderateWalks) >= Number(data.ModerateWalksCount);
      console.log(
        "Mod Walks:",
        moderateWalks,
        "Total Mod Walks:",
        data.ModerateWalksCount
      );

      this.badges.strenuous =
        Number(strenuousWalks) >= Number(data.StrenuousWalksCount);
      console.log(
        "Strn Walks:",
        strenuousWalks,
        "Total Strn Walks:",
        data.StrenuousWalksCount
      );

      this.badges.all =
        Number(easyWalks) >= Number(data.EasyWalksCount) &&
        Number(moderateWalks) >= Number(data.ModerateWalksCount) &&
        Number(strenuousWalks) >= Number(data.StrenuousWalksCount);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return isNaN(date) ? "Invalid Date" : date.toLocaleDateString();
    },
    async sharePage(imageUrl) {
      // Check if the browser supports the share API with files
      if (navigator.share && navigator.canShare) {
        try {
          // Fetch the badge image as a blob
          const response = await fetch(imageUrl);
          const blob = await response.blob();

          // Create a File object from that blob
          const file = new File([blob], imageUrl.split("/").pop(), {
            type: blob.type,
          });

          // Check if the browser can share files of this type
          if (!navigator.canShare({ files: [file] })) {
            alert("File sharing is not supported on this browser/device.");
            return;
          }

          await navigator.share({
            title: document.title,
            text: "Check out the badge I earned on Wicklow Trails",
            url: "https://wicklowtrails.ie",
            files: [file],
          });

          console.log("Successfully shared!");
        } catch (error) {
          console.error("Error sharing:", error);
        }
      } else {
        alert("Sharing not supported on this browser.");
      }
    },
    async sharePageWalk(name) {
      if (navigator.share) {
        try {
          await navigator.share({
            title: document.title,
            text: `I finished ${name} on `,
            url: "https://wicklowtrails.ie",
          });
          console.log("Successfully shared!");
        } catch (error) {
          console.error("Error sharing:", error);
        }
      } else {
        alert("Sharing not supported on this browser.");
      }
    },
  },
};
</script>

<style scoped>
.completed-walks {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.info-box,
.table-container {
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  width: 100%;
  box-sizing: border-box;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

th,
td {
  border: 1px solid #ccc;
  padding: 12px;
  text-align: left;
}

th {
  background-color: #f4f4f4;
  font-weight: bold;
}

td {
  background-color: #ffffff;
}

h1,
h2 {
  text-align: center;
  color: #333;
}

p {
  text-align: center;
  color: #666;
}

a {
  color: #007bff;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.badge-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.badge {
  width: 250px; /* Adjust the size as needed */
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease; /* Optional for hover effects */
}

.badge img {
  width: 100%;
  height: auto;
}

.badge:hover {
  transform: scale(1.1); /* Optional hover effect */
}

.badge.earned {
  /* The parent is relative */
  position: relative;
  display: inline-block; /* or block, depending on your layout needs */
}

/* Make sure the badge image doesn't have extra padding around it */
.badge.earned img {
  display: block;
}

.special-badge {
  margin-top: 20px;
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
}

.share-button-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: -30px;
  position: absolute;
  z-index: 10;
  right: -9px;
  top: -17px;
}

.share-button-wrapper a {
  display: flex;
  align-items: center;
  margin-top: 3px;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 50%;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.5);
  transition: 0.3s ease-in-out;
}

.share-button-wrapper img {
  width: 30px;
  height: 30px;
  filter: invert(0.5); /* Darker icon */
}

/* Hover Effect */
.share-button-wrapper a:hover {
  background: #007bff;
}

.share-button-wrapper a:hover img {
  filter: invert(1); /* White icon on hover */
}

.share-button-wrapper2 {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0px;

  z-index: 10;
  right: 0px;
  left: 0px;
  top: 0px;
}

.share-button-wrapper2 a {
  display: flex;
  align-items: center;
  margin-top: 3px;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 50%;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.5);
  transition: 0.3s ease-in-out;
}

.share-button-wrapper2 img {
  width: 30px;
  height: 30px;
  filter: invert(0.5); /* Darker icon */
}

/* Hover Effect */
.share-button-wrapper2 a:hover {
  background: #007bff;
}

.share-button-wrapper2 a:hover img {
  filter: invert(1); /* White icon on hover */
}

/* === Make it smaller on mobile === */
@media (max-width: 600px) {
  .share-button-wrapper2 a {
    width: 40px;
    height: 40px;
  }

  .share-button-wrapper2 img {
    width: 20px;
    height: 20px;
  }
}
</style>
